import { z } from 'zod';
import { FIELD_TYPES } from '../fields/values.js';

/**
 * Field definitions and tags, as admins edit them (Settings → Fields / Tags). The database enforces
 * the same limits (check constraints and triggers in the M3 migration).
 */

export const FieldLabelSchema = z
  .string()
  .trim()
  .min(1, 'Enter a label')
  .max(60, 'Use at most 60 characters');

export const FieldOptionsSchema = z
  .array(
    z
      .string()
      .trim()
      .min(1, 'Options cannot be empty')
      .max(100, 'Keep options under 100 characters'),
  )
  .max(100, 'Use at most 100 options')
  .refine(
    (opts) => new Set(opts.map((o) => o.toLowerCase())).size === opts.length,
    'Each option must be different',
  );

/** Matches the `field_definitions.key` check constraint. Keys never change once created. */
export const FieldKeySchema = z.string().regex(/^[a-z][a-z0-9_]{0,47}$/);

export const NewFieldSchema = z
  .object({
    label: FieldLabelSchema,
    type: z.enum(FIELD_TYPES, { error: 'Choose a type' }),
    options: FieldOptionsSchema,
  })
  .refine((f) => f.type !== 'dropdown' || f.options.length > 0, {
    message: 'Add at least one option',
    path: ['options'],
  });
export type NewField = z.infer<typeof NewFieldSchema>;

/** Splits the options textarea (one per line) into a clean list. */
export function parseOptionLines(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line !== '');
}

/**
 * A key for a new field from its label ("Monthly revenue" → "monthly_revenue"), made unique against
 * the workspace's existing keys (archived fields included) with a numeric suffix.
 */
export function fieldKeyFromLabel(label: string, existingKeys: Iterable<string>): string {
  let base = label
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (!/^[a-z]/.test(base)) base = `field_${base}`.replace(/_+$/, '');
  base = base.slice(0, 40).replace(/_+$/, '');
  const taken = new Set(existingKeys);
  if (!taken.has(base)) return base;
  for (let n = 2; ; n += 1) {
    const candidate = `${base}_${n}`;
    if (!taken.has(candidate)) return candidate;
  }
}

export const TAG_COLORS = [
  'gray',
  'red',
  'orange',
  'amber',
  'green',
  'teal',
  'blue',
  'indigo',
  'purple',
  'pink',
] as const;
export type TagColor = (typeof TAG_COLORS)[number];

export const TagNameSchema = z
  .string()
  .trim()
  .min(1, 'Enter a tag name')
  .max(40, 'Use at most 40 characters');

export const TagFormSchema = z.object({
  name: TagNameSchema,
  color: z.enum(TAG_COLORS, { error: 'Choose a color' }),
});
export type TagForm = z.infer<typeof TagFormSchema>;

/** The system tag every workspace has; it cannot be renamed or deleted. */
export const INTERESTED_TAG = 'Interested';
